import React, { useEffect, useState, Component } from "react";
import GskLoginlogo from "../assests/gsk_1.svg";
import bgImg from "../assests/bg-gsk-image.png";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import MailIcon from "@mui/icons-material/Mail";
import { useNavigate } from "react-router-dom";
import axiosRequest from "../api/axios";
import CircularProgress from "@mui/material/CircularProgress";
import { config } from "../Config";
import { getUserProfile, normalizeError } from "./GetUserProfile";
import { UserAgentApplication } from "msal";
import ErrorMessage from "./ErrorMessage";
import { TurnedInSharp } from "@mui/icons-material";
import useAuth from "./hooks/useAuth";
import { Navigate } from "react-router-dom";
import { useHistory } from "react-router-dom";

class Login extends Component {
    constructor(props) {
        super(props);
        this.logoutTimer = null;
        this.inactivityInterval = null;
        this.inactivityTimeout = 2 * 60 * 1000; // 2 minutes in milliseconds
        this.toggle = this.toggle.bind(this);
        this.setAuth = props.useAuth;
        this.navigate = props.navigation;
        this.history = props.useHistory;
        this.state = {
            isOpen: false,
            error: null,
            role:
                localStorage.getItem("Auth") === "true"
                    ? localStorage.getItem("ROLE")
                    : "",
            user: {},
            redirect: "/login",
            loader: false,
            lastActivity: Date.now(),
            timeLeft: this.inactivityTimeout / 1000,
        };

        // Bind methods
        this.resetLogoutTimer = this.resetLogoutTimer.bind(this);
        this.startLogoutTimer = this.startLogoutTimer.bind(this);
        this.logout = this.logout.bind(this);
        this.handleUserActivity = this.handleUserActivity.bind(this);
        this.checkInactivity = this.checkInactivity.bind(this);
        this.forceLogout = this.forceLogout.bind(this);

        this.userAgentApplication = null;
    }

    _getMsalApp() {
        if (!this.userAgentApplication) {
            this.userAgentApplication = new UserAgentApplication({
                auth: {
                    clientId: config.appId,
                    redirectUri: config.redirectUri,
                    authority: config.authority,
                },
                cache: {
                    cacheLocation: "sessionStorage",
                    storeAuthStateInCookie: false,
                },
            });
        }
        return this.userAgentApplication;
    }

    componentDidMount() {
        window._isLoginComponentMounted = true;

        if (localStorage.getItem("Auth") === "true") {
            if (!localStorage.getItem("sessionTimeoutAt")) {
                const expirationTime = Date.now() + this.inactivityTimeout;
                localStorage.setItem(
                    "sessionTimeoutAt",
                    expirationTime.toString(),
                );
            }
            this.setupAutoLogout();
        }
    }

    setupAutoLogout() {
        this.clearAllTimers();

        // Set up activity listeners
        document.addEventListener("mousemove", this.handleUserActivity);
        document.addEventListener("mousedown", this.handleUserActivity);
        document.addEventListener("keypress", this.handleUserActivity);
        document.addEventListener("touchmove", this.handleUserActivity);
        document.addEventListener("scroll", this.handleUserActivity);

        // Set up the main logout timer
        this.startLogoutTimer();

        // Set up countdown and checking interval (every 1 second)
        this.inactivityInterval = setInterval(() => {
            this.checkInactivity();

            const timeoutAt = parseInt(
                localStorage.getItem("sessionTimeoutAt") || "0",
            );
            const timeLeft = Math.max(
                0,
                Math.floor((timeoutAt - Date.now()) / 1000),
            );
            this.setState({ timeLeft });
        }, 1000);
    }

    componentWillUnmount() {
        window._isLoginComponentMounted = false;
        this.clearAllTimers();
    }

    clearAllTimers() {
        if (this.logoutTimer) {
            clearTimeout(this.logoutTimer);
            this.logoutTimer = null;
        }

        if (this.inactivityInterval) {
            clearInterval(this.inactivityInterval);
            this.inactivityInterval = null;
        }

        // Remove event listeners
        document.removeEventListener("mousemove", this.handleUserActivity);
        document.removeEventListener("mousedown", this.handleUserActivity);
        document.removeEventListener("keypress", this.handleUserActivity);
        document.removeEventListener("touchmove", this.handleUserActivity);
        document.removeEventListener("scroll", this.handleUserActivity);
    }

    forceLogout() {
        if (localStorage.getItem("Auth") === "true") {
            this.logout();
        }
    }

    // Record user activity
    handleUserActivity() {
        if (!localStorage.getItem("Auth") === "true") return;

        // Update last activity time
        const now = Date.now();
        this.setState({ lastActivity: now });

        // Update session timeout in localStorage
        const newExpirationTime = now + this.inactivityTimeout;
        localStorage.setItem("sessionTimeoutAt", newExpirationTime.toString());

        // Reset the logout timer
        this.resetLogoutTimer();
    }

    checkInactivity() {
        if (!localStorage.getItem("Auth") === "true") return;

        const now = Date.now();
        const timeoutAt = parseInt(
            localStorage.getItem("sessionTimeoutAt") || "0",
        );

        if (now >= timeoutAt) {
            this.logout();
        }
    }

    startLogoutTimer() {
        if (this.logoutTimer) {
            clearTimeout(this.logoutTimer);
            this.logoutTimer = null;
        }

        const timeoutAt = parseInt(
            localStorage.getItem("sessionTimeoutAt") || "0",
        );
        const timeUntilExpiry = Math.max(0, timeoutAt - Date.now());

        this.logoutTimer = setTimeout(() => {
            this.forceLogout();
        }, timeUntilExpiry);
    }

    resetLogoutTimer() {
        // Clear existing timer and start a new one
        this.startLogoutTimer();
    }

    toggle() {
        this.setState({
            isOpen: !this.state.isOpen,
        });
    }

    async getQuarter() {
        await axiosRequest({
            url: "bu_head_app/getquarter",
            method: "GET",
        }).then((data) => {
            if (data.status === 200) {
                localStorage.setItem("quarter", data?.data?.data[0]["quarter"]);
                localStorage.setItem("year", data?.data?.data[0]["year"]);
            }
        });
    }

    async login() {
        this.setState({ loader: true });

        try {
            const msalApp = this._getMsalApp();
            await msalApp.loginPopup({
                scopes: config.scopes,
                prompt: "select_account",
            });

            const user = await getUserProfile(msalApp, config.scopes);

            this.setState({
                user: { token: user.access_token },
                error: null,
                lastActivity: Date.now(),
            });

            await axiosRequest({
                url: "users/token/",
                method: "POST",
                data: { token: user },
            }).then((data) => {
                if (data.status === 200) {
                    this.setState({ role: data?.data?.role });

                    // Set session data
                    localStorage.setItem("Auth", "true");
                    localStorage.setItem("ROLE", data?.data?.role);
                    localStorage.setItem("username", data?.data?.employee_name);
                    localStorage.setItem("email", data?.data?.email);
                    localStorage.setItem("teamcode", data?.data?.team_code);
                    localStorage.setItem("Token", data?.data?.access);
                    localStorage.setItem("abmcode", data?.data?.abm_code);
                    localStorage.setItem("rbmcode", data?.data?.rbm_code);
                    localStorage.setItem("rephqcode", data?.data?.hq_code);
                    localStorage.setItem("dtcode", data?.data?.dt_code);
                    localStorage.setItem("rbmscreen", data?.data?.rbm_screen);
                    localStorage.setItem("quarter", "Q4");
                    localStorage.setItem("year", "2024");

                    // Set session timeout
                    const expirationTime = Date.now() + this.inactivityTimeout;
                    localStorage.setItem(
                        "sessionTimeoutAt",
                        expirationTime.toString(),
                    );
                    localStorage.setItem("loginTime", Date.now().toString());

                    this.getQuarter();

                    // Initialize auto-logout system
                    this.setupAutoLogout();

                    // Update component state and redirect
                    if (data?.data?.role === "BUH") {
                        this.setState({ setAuth: "BUH", loader: false });
                        return <Navigate replace to="/rbmlevelbudget" />;
                    } else if (data?.data?.role === "ADMIN") {
                        this.setState({ setAuth: "ADMIN", loader: false });
                    } else if (data?.data?.role === "NOROLE") {
                        this.setState({ setAuth: "NOROLE", loader: false });
                    } else if (data?.data?.role === "RBM") {
                        this.setState({ setAuth: "RBM", loader: false });
                    } else if (data?.data?.role === "ABM") {
                        this.setState({ setAuth: "ABM", loader: false });
                    } else if (data?.data?.role === "REPHQ") {
                        this.setState({ setAuth: "REPHQ", loader: false });
                    } else if (data?.data?.role === "COMMERCIALHEAD") {
                        this.setState({
                            setAuth: "COMMERCIALHEAD",
                            loader: false,
                        });
                    }
                } else {
                    this.setState({ loader: false });
                    return <Navigate replace to="/login" />;
                }
            });
        } catch (err) {
            // Ignore expected cancellation when user closes the popup
            if (err && err.errorCode !== "user_cancelled") {
                this.setState({
                    isAuthenticated: false,
                    user: {},
                    error: normalizeError(err),
                    loader: false,
                });
            } else {
                this.setState({ loader: false });
            }
        }
    }

    handleSubmit = (event) => {
        event.preventDefault();
    };

    logout() {
        this.clearAllTimers();

        localStorage.removeItem("Auth");
        localStorage.removeItem("ROLE");
        localStorage.removeItem("username");
        localStorage.removeItem("email");
        localStorage.removeItem("teamcode");
        localStorage.removeItem("Token");
        localStorage.removeItem("sessionTimeoutAt");
        localStorage.removeItem("loginTime");

        if (this.userAgentApplication) {
            try {
                this.userAgentApplication.logout();
            } catch (error) {
                // silent — logout errors are non-critical
            }
        }

        this.setState({ role: "", user: {} });
        alert(
            "Your session has expired due to inactivity. Please log in again.",
        );
        window.location.href = "/login";
    }

    render() {
        let error = null;
        if (this.state.error) {
            error = (
                <ErrorMessage
                    message={this.state.error.message}
                    debug={this.state.error.debug}
                />
            );
        }

        // Development only: display countdown timer
        let debugInfo = null;
        if (process.env.NODE_ENV === "development") {
            debugInfo = (
                <div
                    style={{
                        position: "fixed",
                        bottom: 10,
                        right: 10,
                        background: "rgba(0,0,0,0.7)",
                        color: "white",
                        padding: "5px 10px",
                        borderRadius: 5,
                    }}
                >
                    Session expires in: {this.state.timeLeft} seconds
                </div>
            );
        }

        if (localStorage.getItem("Auth") === "true") {
            if (this.state.role === "BUH") {
                return <Navigate replace to="/rbmlevelbudget" />;
            } else if (this.state.role === "ADMIN") {
                return <Navigate replace to="/admin/head_count" />;
            } else if (this.state.role === "NOROLE") {
                return <Navigate replace to="/role" />;
            } else if (this.state.role === "COMMERCIALHEAD") {
                return <Navigate replace to="/commercialheadtable" />;
            } else if (this.state.role === "RBM") {
                return <Navigate replace to="/abmselection" />;
            } else if (this.state.role === "ABM") {
                return <Navigate replace to="/rephqselection" />;
            } else if (this.state.role === "REPHQ") {
                return <Navigate replace to="/rephqlevelbudget" />;
            }
        }

        return (
            <div
                style={{
                    width: "100%",
                    height: "100vh",
                    backgroundImage: `url(${require("../assests/bg-gsk-image.png")})`,
                    backgroundSize: "cover",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {debugInfo}
                <div
                    className="login-card"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <img src={GskLoginlogo} alt="" />
                    <button
                        onClick={() => this.login()}
                        style={{
                            width: "150px",
                            background:
                                "linear-gradient(to right,#eb7f25, #ae391e)",
                            border: "none",
                            height: "35px",
                            color: "white",
                            borderRadius: "20px",
                            fontWeight: "bold",
                        }}
                    >
                        {this.state.loader ? (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <CircularProgress size="1rem" color="inherit" />
                            </div>
                        ) : (
                            <span>SIGN IN</span>
                        )}
                    </button>
                </div>
            </div>
        );
    }
}

export default Login;
